export interface AgentConfig {
  name: string
  displayName: string
  path: string
  color?: string
  description?: string
  features?: string[]
}

export const AGENT_CONFIGS: Record<string, AgentConfig> = {
  tyrosai: {
    name: 'tyrosai',
    displayName: 'Tyros AI',
    path: '/tyrosai',
    color: 'bg-aw-primary',
    description: 'Advanced AI assistant for general inquiries',
    features: ['chat', 'analysis', 'recommendations']
  },
  routerai: {
    name: 'routerai',
    displayName: 'Router AI',
    path: '/routerai',
    color: 'bg-gradient-to-r from-aw-primary to-aw-secondary',
    description: 'Intelligent agent router that connects you to the best specialized AI for your needs',
    features: ['agent-routing', 'request-analysis', 'agent-recommendation', 'handoff'],
  },
  pmoai: {
    name: 'pmoai',
    displayName: 'PMO AI',
    path: '/pmoai',
    color: 'bg-aw-error',
    description: 'Project management office and issue management AI',
    features: ['issue-tracking', 'delivery-ops', 'project-management']
  },
  repomanager: {
    name: 'repomanager',
    displayName: 'Repo Manager',
    path: '/repomanager',
    color: 'bg-aw-accent',
    description: 'Repository and code management assistant',
    features: ['repository-management', 'code-analysis', 'version-control']
  },
  dama: {
    name: 'dama',
    displayName: 'DAMA Tech Solutions',
    path: '/dama/quotation',
    color: 'bg-aw-secondary',
    description: 'Data analysis and quotation management',
    features: ['data-analysis', 'quotations', 'business-intelligence']
  },
  menagri: {
    name: 'menagri',
    displayName: 'Menagri',
    path: '/menagri/trial_evaluation',
    color: 'bg-aw-success',
    description: 'Agricultural management and trial evaluation',
    features: ['agriculture', 'trial-evaluation', 'farming-analytics']
  },
  pyramidre: {
    name: 'pyramidre',
    displayName: 'Pyramid RE',
    path: '/pyramidre',
    color: 'bg-aw-accent',
    description: 'Real estate management and analysis',
    features: ['real-estate', 'property-management', 'market-analysis']
  },
  mediapro: {
    name: 'mediapro',
    displayName: 'Media Pro',
    path: '/mediapro',
    color: 'bg-aw-success',
    description: 'Promotional and marketing assistant',
    features: ['marketing', 'promotion', 'content-creation']
  },
  unknown: {
    name: 'unknown',
    displayName: 'Unknown Agent',
    path: '/',
    color: 'bg-aw-text-muted',
    description: 'Default fallback for unrecognized agents',
    features: []
  }
}

/* ====================================================================
   Agent Routing Helper Functions
   ==================================================================== */

/**
 * Maps agent names to their routes based on available agents from AGENT_CONFIGS
 */
export function getAgentRoute(agentName: string): string | null {
  const normalizedName = agentName.toLowerCase().trim();
  
  // Direct match first
  if (AGENT_CONFIGS[normalizedName]) {
    return AGENT_CONFIGS[normalizedName].path;
  }
  
  // Try alternative names/aliases
  const aliasMap: Record<string, string> = {
    'tyros ai': 'tyrosai',
    'router ai': 'routerai',
    'issue manager': 'pmoai',
    'deliveryops ai': 'pmoai',
    'pmo ai': 'pmoai',
    'project management office': 'pmoai',
    'repo manager': 'repomanager',
    'dama tech solutions': 'dama',
  };
  
  const aliasKey = aliasMap[normalizedName];
  if (aliasKey && AGENT_CONFIGS[aliasKey]) {
    return AGENT_CONFIGS[aliasKey].path;
  }
  
  return null;
}

/**
 * Get agent configuration by name with fallback to unknown
 */
export function getAgentConfig(agentName?: string): AgentConfig {
  if (!agentName) {
    return AGENT_CONFIGS.unknown
  }
  return AGENT_CONFIGS[agentName as keyof typeof AGENT_CONFIGS] || AGENT_CONFIGS.unknown
}

/**
 * Get all available agents
 */
export function getAllAgents(): AgentConfig[] {
  return Object.values(AGENT_CONFIGS).filter(agent => agent.name !== 'unknown')
}

/**
 * Check if an agent exists
 */
export function isValidAgent(agentName: string): boolean {
  return agentName in AGENT_CONFIGS && agentName !== 'unknown'
}
